import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Fade,
  TextField,
  Autocomplete,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AppButton from "./AppButton";
import theme from "../utils/theme";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import { enGB } from "date-fns/locale";

const styles = {
  dialogPaper: {
    borderRadius: 3,
    bgcolor: theme.palette.background.default,
    boxShadow: 10,
    width: 1100,
    maxWidth: "90vw",
    maxHeight: "98vh",
    mt: 8,
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    height: 32,
  },
  titleText: {
    fontWeight: 600,
    fontSize: "1.15em",
    lineHeight: 1,
    display: "inline-block",
    verticalAlign: "middle",
  },
  dialogTitle: {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    paddingTop: 0,
    maxHeight: { xs: "60vh", sm: "70vh", md: "none" },
    overflowY: { xs: "auto", sm: "auto", md: "visible" },
  },
  filterContainer: {
    width: 280,
    minHeight: 40,
  },
  textField: {
    bgcolor: theme.palette.background.paper,
    borderRadius: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  datePicker: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    "& .MuiInputLabel-root": {
      // Force the label to always behave like the field is focused/filled
      "&.Mui-focused, &.MuiFormLabel-filled": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
      // Initial state (before selection)
      "&:not(.Mui-focused):not(.MuiFormLabel-filled)": {
        transform: "translate(14px, 8px)", // Adjust these values as needed
      },
    },
    "& .MuiPickersSectionList-root": {
      backgroundColor: theme.palette.background.default,
      padding: "8px",
    },
    "& .MuiPickersInputBase-sectionsContainer": {
      gap: "8px",
      // Add minimum height to prevent label overlap
      minHeight: "40px", // Adjust based on your design
    },
  },
  dialogActions: {
    px: 3,
    pb: 2,
    display: "flex",
    alignItems: "center",
  },
};

// Dummy options for "onlySelect" filter type
const getDummyOptions = (fieldId) => {
  switch (fieldId) {
    default:
      return [
        { value: "option1", label: "Option 1" },
        { value: "option2", label: "Option 2" },
      ];
  }
};

export default function DashboardTableFilterModal({
  columns,
  open,
  fieldOptions = {},
  loadingStates = {},
  handleSearchChange,
  handleFilterChange,
  getSelectedOptions,
  handleApplyFilters,
  handleResetFilters,
  closeModal,
  searchTerms,
  dateValues,
  handleClearDate,
  handleDateChange,
}) {
  const renderFilterComponent = (col) => {
    const options =
      col.filterType === "onlySelect"
        ? getDummyOptions(col.id)
        : fieldOptions[col.id] || [];
    const selectedOptions = getSelectedOptions(col.id, col.filterType) || [];
    const isLoading = loadingStates.options?.[col.id] || false;
    const isSearching = loadingStates.search?.[col.id] || false;
    const inputValue = searchTerms[col.id] || "";

    if (col.filterType === "date") {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            label={col.label}
            value={dateValues[col.id] || null}
            onChange={(date) => handleDateChange(col.id, date, col.filterType)}
            sx={{ ...styles.datePicker }}
            inputFormat="dd-MM-yyyy"
            mask="__-__-____"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{ ...styles.textField }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {dateValues[col.id] ? (
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleClearDate(col.id, e, col.filterType)
                          }
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <CalendarTodayIcon fontSize="small" color="action" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    return (
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        inputValue={col.filterType === "searchSelect" ? inputValue : ""}
        onInputChange={(_, value, reason) => {
          if (col.filterType === "searchSelect" && reason !== "reset") {
            handleSearchChange(col.id, value);
          }
        }}
        onChange={(_, selected) => {
          handleFilterChange(col.id, selected, col.filterType);
        }}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        filterOptions={
          col.filterType === "onlySelect" ? undefined : (options) => options
        }
        disableClearable={col.filterType === "onlySelect"}
        renderInput={(params) => (
          <TextField
            {...params}
            label={col.label}
            variant="outlined"
            size="small"
            sx={styles.textField}
            disabled={loadingStates.global || isLoading}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {col.filterType === "searchSelect" &&
                  (isSearching || isLoading) ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      BackdropProps={{ style: { background: "rgba(40, 0, 0, 0.5)" } }}
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleBox}>
          <FilterListIcon />
          <Typography variant="h5">Filters</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {/* {loadingStates.global ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography
              variant="h5"
              sx={{ mb: 3, color: theme.palette.primary.main }}
            >
              Loading Filters...
            </Typography>
            <CircularProgress />
          </Box>
        ) : ( */}
        <Grid container spacing={3}>
          {columns?.map((col) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={col.id}>
              <Box sx={styles.filterContainer}>
                {renderFilterComponent(col)}
              </Box>
            </Grid>
          ))}
        </Grid>
        {/* )} */}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <AppButton
          onClick={handleResetFilters}
          label="Reset"
          buttonType="greyed"
          disabled={loadingStates.global}
        />
        <Box sx={{ flex: 1 }} />
        <AppButton
          onClick={closeModal}
          label="Cancel"
          buttonType="greyed"
          disabled={loadingStates.global}
        />
        <AppButton
          onClick={handleApplyFilters}
          label={loadingStates.global ? "Loading Filters..." : "Apply"}
          buttonType="primary"
          disabled={loadingStates.global}
        />
      </DialogActions>
    </Dialog>
  );
}
